import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { apiClient } from '../lib/api-client';
import { formatDateTime } from '../utils/datetime';

type TicketDetail = {
  id: string;
  code: string;
  planId: string;
  plan: {
    id: string;
    tier: string;
    name: string;
    displayName: Record<string, string>;
    limits: Record<string, number | boolean | null>;
  };
  issuer: {
    id: string;
    did: string;
    handle: string;
    displayName: string | null;
  };
  issuedAt: string;
  redemptionDeadline: string | null;
  validDays: number;
  planExpiresAt: string | null;
  maxRedemptions: number | null;
  status: string;
  memo: string | null;
  limitsOverride: Record<string, number | boolean | null> | null;
  createdAt: string;
  updatedAt: string;
};

type Assignment = {
  id: string;
  user: {
    id: string;
    did: string;
    handle: string;
    displayName: string | null;
  };
  activatedAt: string;
  expiresAt: string | null;
  status: string;
  createdAt: string;
};

const statusStyles: Record<string, string> = {
  UNUSED:
    'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
  ACTIVE:
    'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
  EXPIRED:
    'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/50 dark:text-gray-400 dark:border-gray-700',
  REVOKED:
    'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
};

type LimitItemProps = {
  label: string;
  planValue: number | boolean;
  overrideValue?: number | boolean | null;
  formatValue?: (value: number | boolean) => string;
};

function LimitItem({ label, planValue, overrideValue, formatValue }: LimitItemProps) {
  const hasOverride = overrideValue !== undefined && overrideValue !== null;
  const displayPlanValue = formatValue ? formatValue(planValue) : String(planValue);
  const displayOverrideValue =
    hasOverride && formatValue ? formatValue(overrideValue) : String(overrideValue);

  return (
    <div className='flex items-baseline justify-between border-b border-gray-100 py-2 last:border-0 dark:border-gray-700'>
      <span className='text-sm text-gray-600 dark:text-gray-400'>{label}</span>
      <div className='flex items-baseline gap-2'>
        {hasOverride ? (
          <>
            <span className='text-sm text-gray-400 line-through dark:text-gray-500'>
              {displayPlanValue}
            </span>
            <span className='font-semibold text-blue-600 dark:text-blue-400'>
              → {displayOverrideValue}
            </span>
          </>
        ) : (
          <span className='font-medium text-gray-900 dark:text-white'>{displayPlanValue}</span>
        )}
      </div>
    </div>
  );
}

function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokingAssignment, setRevokingAssignment] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (ticketId) {
      loadTicket();
    }
  }, [ticketId]);

  const loadTicket = async () => {
    if (!ticketId) return;

    try {
      setLoading(true);
      const data = await apiClient.getTicket(ticketId);
      console.log('[TicketDetailPage] API Response:', data);
      console.log('[TicketDetailPage] Assignments:', data.assignments);
      if (data.assignments.length > 0) {
        console.log('[TicketDetailPage] First assignment user:', data.assignments[0].user);
      }
      setTicket(data.ticket);
      setAssignments(data.assignments);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!ticketId || !ticket) return;

    if (!confirm(`Are you sure you want to revoke ticket ${ticket.code}?`)) {
      return;
    }

    try {
      setRevoking(true);
      await apiClient.revokeTicket(ticketId);
      await loadTicket(); // Reload to get updated status
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke ticket');
    } finally {
      setRevoking(false);
    }
  };

  const handleRevokeAssignment = async (assignmentId: string, userName: string) => {
    if (!confirm(`Are you sure you want to revoke the plan assignment for ${userName}?`)) {
      return;
    }

    try {
      setRevokingAssignment(assignmentId);
      // Extract userId from assignment
      const assignment = assignments.find(a => a.id === assignmentId);
      if (!assignment) {
        throw new Error('Assignment not found');
      }

      await apiClient.revokeUserPlanAssignment(assignment.user.id, assignmentId);
      await loadTicket(); // Reload to get updated assignments
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke assignment');
    } finally {
      setRevokingAssignment(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  if (loading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='text-lg text-gray-600 dark:text-gray-400'>Loading ticket...</div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className='space-y-4'>
        <button
          onClick={() => navigate('/tickets')}
          className='text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
        >
          ← Back to Tickets
        </button>
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <p className='text-red-800 dark:text-red-400'>Error: {error || 'Ticket not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <button
            onClick={() => navigate('/tickets')}
            className='text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
          >
            ← Back
          </button>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Ticket Details</h1>
        </div>
        {ticket.status === 'UNUSED' && (
          <button
            onClick={handleRevoke}
            disabled={revoking}
            className='rounded-md bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:bg-red-700 dark:hover:bg-red-600 dark:disabled:bg-gray-700'
          >
            {revoking ? 'Revoking...' : 'Revoke Ticket'}
          </button>
        )}
      </div>

      {/* Ticket Information Card */}
      <div className='space-y-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800 dark:shadow-gray-900/50'>
        <div className='flex items-start justify-between'>
          <div>
            <h2 className='font-mono text-2xl font-bold text-gray-900 dark:text-white'>
              {ticket.code}
            </h2>
            <button
              onClick={() => copyToClipboard(ticket.code)}
              className='mt-1 text-sm text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300'
            >
              Copy coupon code
            </button>
          </div>
          <span
            className={`rounded-md border px-4 py-2 text-sm font-semibold ${
              statusStyles[ticket.status] ||
              'border-gray-200 bg-gray-100 text-gray-800 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400'
            }`}
          >
            {ticket.status}
          </span>
        </div>

        <div className='grid grid-cols-2 gap-6'>
          {/* Plan Information */}
          <div>
            <h3 className='mb-2 text-sm font-medium text-gray-500 dark:text-gray-400'>Plan</h3>
            <div className='text-lg font-semibold text-gray-900 dark:text-white'>
              {ticket.plan.displayName?.en || ticket.plan.name}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>{ticket.plan.tier}</div>
          </div>

          {/* Issuer Information */}
          <div>
            <h3 className='mb-2 text-sm font-medium text-gray-500 dark:text-gray-400'>Issuer</h3>
            <div className='text-lg font-semibold text-gray-900 dark:text-white'>
              {ticket.issuer.displayName || ticket.issuer.handle}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>@{ticket.issuer.handle}</div>
          </div>

          {/* Valid Period */}
          <div>
            <h3 className='mb-2 text-sm font-medium text-gray-500 dark:text-gray-400'>
              Valid Period
              {ticket.planExpiresAt && (
                <span className='ml-2 rounded-md bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'>
                  Absolute
                </span>
              )}
            </h3>
            {ticket.planExpiresAt ? (
              <>
                <div className='text-lg font-semibold text-gray-900 dark:text-white'>
                  {formatDateTime(ticket.planExpiresAt, {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  All users expire on this date
                </div>
                <div className='mt-1 text-xs text-gray-500 dark:text-gray-500'>
                  (Reference: {ticket.validDays} days from activation - ignored)
                </div>
              </>
            ) : (
              <>
                <div className='text-lg font-semibold text-gray-900 dark:text-white'>
                  {ticket.validDays} days
                </div>
                <div className='text-sm text-gray-600 dark:text-gray-400'>
                  from each user&apos;s activation date
                </div>
              </>
            )}
          </div>

          {/* Redemption Limits */}
          <div>
            <h3 className='mb-2 text-sm font-medium text-gray-500 dark:text-gray-400'>
              Redemption Limits
            </h3>
            <div className='text-lg font-semibold text-gray-900 dark:text-white'>
              {assignments.length} / {ticket.maxRedemptions ?? '∞'} used
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>
              {ticket.maxRedemptions !== null
                ? `${ticket.maxRedemptions - assignments.length} remaining`
                : 'Unlimited'}
            </div>
          </div>

          {/* Issued At */}
          <div>
            <h3 className='mb-2 text-sm font-medium text-gray-500 dark:text-gray-400'>Issued At</h3>
            <div className='text-lg font-semibold text-gray-900 dark:text-white'>
              {formatDateTime(ticket.issuedAt, { dateStyle: 'medium', timeStyle: 'short' }) ||
                'N/A'}
            </div>
          </div>

          {/* Redemption Deadline */}
          <div>
            <h3 className='mb-2 text-sm font-medium text-gray-500 dark:text-gray-400'>
              Redemption Deadline
            </h3>
            <div className='text-lg font-semibold text-gray-900 dark:text-white'>
              {formatDateTime(ticket.redemptionDeadline, {
                dateStyle: 'medium',
                timeStyle: 'short'
              }) || 'N/A'}
            </div>
          </div>

          {/* Last Updated */}
          <div>
            <h3 className='mb-2 text-sm font-medium text-gray-500 dark:text-gray-400'>
              Last Updated
            </h3>
            <div className='text-lg font-semibold text-gray-900 dark:text-white'>
              {formatDateTime(ticket.updatedAt, { dateStyle: 'medium', timeStyle: 'short' }) ||
                'N/A'}
            </div>
          </div>
        </div>

        {/* Memo */}
        {ticket.memo && (
          <div>
            <h3 className='mb-2 text-sm font-medium text-gray-500 dark:text-gray-400'>Memo</h3>
            <div className='rounded-md bg-gray-50 p-3 text-gray-900 dark:bg-gray-900/50 dark:text-gray-200'>
              {ticket.memo}
            </div>
          </div>
        )}
      </div>

      {/* Plan Limits Section */}
      <div className='space-y-4 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800 dark:shadow-gray-900/50'>
        <div className='flex items-center justify-between'>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>Plan Limits</h2>
          {ticket.limitsOverride &&
            Object.keys(ticket.limitsOverride).length > 0 &&
            Object.values(ticket.limitsOverride).some(v => v !== null) && (
              <span className='rounded-md bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'>
                {Object.values(ticket.limitsOverride).filter(v => v !== null).length} Override(s)
              </span>
            )}
        </div>

        <div className='grid gap-6 md:grid-cols-2'>
          {/* Posts & Scheduling Limits */}
          <div className='space-y-2 rounded-md border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-900/30'>
            <h3 className='mb-3 font-semibold text-gray-700 dark:text-gray-300'>
              Posts & Scheduling
            </h3>
            <LimitItem
              label='Monthly Posts Limit'
              planValue={(ticket.plan.limits.monthlyPostsLimit ?? 0) as number}
              overrideValue={ticket.limitsOverride?.monthlyPostsLimit as number | undefined}
              formatValue={v => (v === -1 ? 'Unlimited' : String(v))}
            />
            <LimitItem
              label='Pending Posts Limit'
              planValue={(ticket.plan.limits.pendingPostsLimit ?? 0) as number}
              overrideValue={ticket.limitsOverride?.pendingPostsLimit as number | undefined}
              formatValue={v => (v === -1 ? 'Unlimited' : String(v))}
            />
            <LimitItem
              label='Max Schedule Days'
              planValue={(ticket.plan.limits.maxScheduleDays ?? 0) as number}
              overrideValue={ticket.limitsOverride?.maxScheduleDays as number | undefined}
              formatValue={v => (v === -1 ? 'Unlimited' : `${v} days`)}
            />
            <LimitItem
              label='Schedule Interval'
              planValue={(ticket.plan.limits.scheduleIntervalMinutes ?? 0) as number}
              overrideValue={ticket.limitsOverride?.scheduleIntervalMinutes as number | undefined}
              formatValue={v => `${v} minutes`}
            />
          </div>

          {/* Content Limits */}
          <div className='space-y-2 rounded-md border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-900/30'>
            <h3 className='mb-3 font-semibold text-gray-700 dark:text-gray-300'>Content</h3>
            <LimitItem
              label='Max Post Length'
              planValue={(ticket.plan.limits.maxPostLength ?? 300) as number}
              overrideValue={ticket.limitsOverride?.maxPostLength as number | undefined}
              formatValue={v => `${v} characters`}
            />
            <LimitItem
              label='Thread Posts'
              planValue={(ticket.plan.limits.threadPosts ?? false) as boolean}
              overrideValue={ticket.limitsOverride?.threadPosts as boolean | undefined}
              formatValue={v => (v ? 'Enabled' : 'Disabled')}
            />
            <LimitItem
              label='Thread Posts Limit'
              planValue={(ticket.plan.limits.threadPostsLimit ?? 1) as number}
              overrideValue={ticket.limitsOverride?.threadPostsLimit as number | undefined}
              formatValue={v => (v === -1 ? 'Unlimited' : String(v))}
            />
            <LimitItem
              label='Markdown Support'
              planValue={(ticket.plan.limits.markdownSupport ?? false) as boolean}
              overrideValue={ticket.limitsOverride?.markdownSupport as boolean | undefined}
              formatValue={v => (v ? 'Enabled' : 'Disabled')}
            />
          </div>

          {/* Image Limits */}
          <div className='space-y-2 rounded-md border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-900/30'>
            <h3 className='mb-3 font-semibold text-gray-700 dark:text-gray-300'>Images</h3>
            <LimitItem
              label='Max Images per Post'
              planValue={(ticket.plan.limits.maxImagesPerPost ?? 4) as number}
              overrideValue={ticket.limitsOverride?.maxImagesPerPost as number | undefined}
              formatValue={v => (v === -1 ? 'Unlimited' : String(v))}
            />
            <LimitItem
              label='Max Image Size'
              planValue={(ticket.plan.limits.maxImageSizeMb ?? 1) as number}
              overrideValue={ticket.limitsOverride?.maxImageSizeMb as number | undefined}
              formatValue={v => `${v} MB`}
            />
          </div>

          {/* Video Limits */}
          <div className='space-y-2 rounded-md border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-900/30'>
            <h3 className='mb-3 font-semibold text-gray-700 dark:text-gray-300'>Video</h3>
            <LimitItem
              label='Video Upload'
              planValue={(ticket.plan.limits.videoUpload ?? false) as boolean}
              overrideValue={ticket.limitsOverride?.videoUpload as boolean | undefined}
              formatValue={v => (v ? 'Enabled' : 'Disabled')}
            />
            <LimitItem
              label='Max Video Size'
              planValue={(ticket.plan.limits.maxVideoSizeMb ?? 0) as number}
              overrideValue={ticket.limitsOverride?.maxVideoSizeMb as number | undefined}
              formatValue={v => `${v} MB`}
            />
            <LimitItem
              label='Video Processing (Monthly)'
              planValue={(ticket.plan.limits.videoProcessingMinutesMonthly ?? 0) as number}
              overrideValue={
                ticket.limitsOverride?.videoProcessingMinutesMonthly as number | undefined
              }
              formatValue={v => (v === -1 ? 'Unlimited' : `${v} minutes`)}
            />
          </div>
        </div>
      </div>

      {/* Assignments Section */}
      <div className='space-y-4 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800 dark:shadow-gray-900/50'>
        <h2 className='text-xl font-bold text-gray-900 dark:text-white'>
          Assignments ({assignments.length})
        </h2>

        {assignments.length === 0 ? (
          <p className='py-8 text-center text-gray-500 dark:text-gray-400'>No assignments yet</p>
        ) : (
          <div className='space-y-4'>
            {assignments.map(assignment => (
              <div
                key={assignment.id}
                className='rounded-md border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-900/30'
              >
                <div className='flex items-start justify-between'>
                  <Link
                    to={`/users/${assignment.user.id}`}
                    className='group flex-1 cursor-pointer transition-opacity hover:opacity-80'
                  >
                    <h3 className='font-semibold text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400'>
                      {assignment.user.displayName || assignment.user.handle}
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      @{assignment.user.handle}
                    </p>
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-500'>
                      {assignment.user.did}
                    </p>
                  </Link>
                  <div className='flex items-center gap-2'>
                    <span
                      className={`rounded-md px-3 py-1 text-xs font-semibold ${
                        assignment.status === 'ACTIVE'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                          : assignment.status === 'EXPIRED'
                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}
                    >
                      {assignment.status}
                    </span>
                    {assignment.status === 'ACTIVE' && (
                      <button
                        onClick={() =>
                          handleRevokeAssignment(
                            assignment.id,
                            assignment.user.displayName || assignment.user.handle
                          )
                        }
                        disabled={revokingAssignment === assignment.id}
                        className='rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:bg-red-700 dark:hover:bg-red-600 dark:disabled:bg-gray-700'
                      >
                        {revokingAssignment === assignment.id ? 'Revoking...' : 'Revoke'}
                      </button>
                    )}
                  </div>
                </div>

                <div className='mt-4 grid grid-cols-3 gap-4 text-sm'>
                  <div>
                    <span className='text-gray-500 dark:text-gray-400'>Activated:</span>
                    <p className='font-medium dark:text-gray-200'>
                      {formatDateTime(assignment.activatedAt, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      }) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className='text-gray-500 dark:text-gray-400'>Expires:</span>
                    <p className='font-medium dark:text-gray-200'>
                      {formatDateTime(assignment.expiresAt, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      }) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className='text-gray-500 dark:text-gray-400'>Created:</span>
                    <p className='font-medium dark:text-gray-200'>
                      {formatDateTime(assignment.createdAt, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      }) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className='fixed right-8 bottom-8 z-50 transition-all duration-300 ease-in-out'>
          <div className='flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-6 py-4 shadow-lg dark:border-green-800 dark:bg-green-900/30 dark:shadow-gray-900'>
            <svg
              className='h-5 w-5 text-green-600 dark:text-green-400'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
            <p className='text-sm font-medium text-green-800 dark:text-green-400'>
              Copied <span className='font-bold'>{ticket.code}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketDetailPage;
